import { useState, useRef, useEffect } from "react";
import { Square, SquareCheck, Trash2 } from "lucide-react";
import type { Notification } from "@/domain";
import { getRelativeTime } from "@/utils/dateFormatters";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils";

interface NotificationItemProps {
	notification: Notification;
	onMarkAsRead: (uuid: string) => void;
	onMarkAsUnread: (uuid: string) => void;
	onDelete: (uuid: string) => void;
	onClick: (notification: Notification) => void;
}

export const NotificationItem = ({
	notification,
	onMarkAsRead,
	onMarkAsUnread,
	onDelete,
	onClick,
}: NotificationItemProps) => {
	const { t } = useTranslation();
	const [isActive, setIsActive] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const deleteTimerRef = useRef<ReturnType<typeof setTimeout>>(null);

	useEffect(() => {
		return () => {
			if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);
		};
	}, []);

	const handleToggleReadStatus = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (notification.is_read) {
			onMarkAsUnread(notification.uuid);
		} else {
			onMarkAsRead(notification.uuid);
		}
	};

	const handleDelete = (e: React.MouseEvent) => {
		e.stopPropagation();
		setIsDeleting(true);
		// Wait for animation to complete before actually deleting
		deleteTimerRef.current = setTimeout(() => {
			onDelete(notification.uuid);
		}, 300);
	};

	return (
		<div
			role="button"
			tabIndex={0}
			className={cn(
				"relative px-3 py-2 cursor-pointer transition-all duration-300 border-b border-border-secondary hover:bg-bg-tertiary/50 text-left",
				!notification.is_read && "bg-violet-light/50",
				isDeleting
					? "opacity-0 -translate-x-full max-h-0 py-0 overflow-hidden"
					: "opacity-100 translate-x-0 max-h-24",
			)}
			onMouseEnter={() => setIsActive(true)}
			onMouseLeave={() => setIsActive(false)}
			onFocus={() => setIsActive(true)}
			onBlur={(e) => {
				if (!e.currentTarget.contains(e.relatedTarget)) {
					setIsActive(false);
				}
			}}
			onClick={() => !isDeleting && onClick(notification)}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					if (!isDeleting) onClick(notification);
				}
			}}
		>
			<div className="flex items-start justify-between gap-2">
				<div className="flex-1 min-w-0">
					<p className="text-xs font-medium text-text-primary line-clamp-1">
						{notification.title}
					</p>
					{notification.body && (
						<p className="text-2xs-plus text-text-secondary mt-0.5 line-clamp-1">
							{notification.body}
						</p>
					)}
					<span className="text-2xs text-text-secondary">
						{getRelativeTime(notification.created_at, t)}
					</span>
				</div>

				{/* Unread dot indicator */}
				{!notification.is_read && !isActive && (
					<div className="w-2 h-2 rounded-full bg-violet-accent flex-shrink-0 mt-1" />
				)}

				{/* Hover/focus actions - joined button group */}
				{isActive && (
					<div className="flex items-center flex-shrink-0 border border-border-primary rounded shadow-sm bg-bg-primary">
						<button
							onClick={handleToggleReadStatus}
							className="w-6 h-6 flex items-center justify-center rounded-l hover:bg-bg-tertiary transition-colors"
							title={
								notification.is_read
									? t("notifications.mark_as_unread")
									: t("notifications.mark_as_read")
							}
						>
							{notification.is_read ? (
								<Square size={12} className="text-text-tertiary" />
							) : (
								<SquareCheck size={12} className="text-text-tertiary" />
							)}
						</button>
						<button
							onClick={handleDelete}
							className="w-6 h-6 flex items-center justify-center rounded-r hover:bg-bg-tertiary transition-colors"
							title={t("notifications.delete")}
						>
							<Trash2 size={12} className="text-text-tertiary" />
						</button>
					</div>
				)}
			</div>
		</div>
	);
};
